import { HttpHeaders, HttpParams, HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Sort } from '@angular/material/sort';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ArrayHelper } from 'tiime-expert-utils';
import { PaginationData, PaginationRange } from 'tiime-material';

import { ApiCommonService } from '@api-services/api-common.service';
import { API_HEADERS, API_ROUTE_BASE, COMPANY_API_ROUTE_BASE } from '@constants/api-routes.constant';
import { ApiAlertError } from '@decorators/api-alert-error';
import { MissionAcceptanceStatus } from '@enums/mission-acceptance-status.enum';
import { FiltersParams, HttpHelper } from '@helpers/http.helper';
import { MissionAcceptance, MissionAcceptanceJson } from '@models/mission-acceptance/mission-acceptance';
import { MissionAcceptanceFile, MissionAcceptanceFileJson } from '@models/mission-acceptance-file';
import { FileSaverData } from '@services/file.service';

/**
 * Mission Acceptance Api Service.
 */
@Injectable({
  providedIn: 'root'
})
export class MissionAcceptanceApiService extends ApiCommonService<MissionAcceptance> {
  readonly resourceUrl: string = `${COMPANY_API_ROUTE_BASE}/mission_acceptances`;
  readonly businessUserResourceUrl: string = `${API_ROUTE_BASE}/users/me`;

  fromJson(json: MissionAcceptanceJson): MissionAcceptance {
    return MissionAcceptance.fromJson(json);
  }

  toJson(model: MissionAcceptance): Partial<MissionAcceptanceJson> {
    return MissionAcceptance.toJson(model);
  }

  @ApiAlertError([HttpStatusCode.Forbidden])
  getMissionAcceptances(): Observable<MissionAcceptance[]> {
    return this.getAll();
  }

  @ApiAlertError()
  getBusinessUserMissionAcceptances(
    range: PaginationRange,
    searchTerms: string,
    sort: Sort,
    filters: FiltersParams
  ): Observable<PaginationData<MissionAcceptance>> {
    const url: string = `${this.businessUserResourceUrl}/mission_acceptances`;
    let params: HttpParams = HttpHelper.setQParam(searchTerms);

    if (filters) {
      params = HttpHelper.setParams(filters, params);
    }
    if (sort) {
      params = HttpHelper.setSortParam(sort, params);
    }

    return this.getPaginated(range, params, url);
  }

  @ApiAlertError()
  getMissionAcceptance(missionAcceptanceId: number, companyId?: number): Observable<MissionAcceptance> {
    let url = `${this.resourceUrl}/${missionAcceptanceId}`;
    const headers = new HttpHeaders({
      Accept: API_HEADERS.missionAcceptanceFilesV2
    });

    if (companyId) {
      url = HttpHelper.replaceCompanyUrl(url, companyId);
    }

    return this.getById(missionAcceptanceId, url, { headers });
  }

  @ApiAlertError()
  createMissionAcceptance(missionAcceptance: MissionAcceptance, companyId: number): Observable<MissionAcceptance> {
    const url: string = `${HttpHelper.replaceCompanyUrl(this.resourceUrl, companyId)}`;
    const headers = new HttpHeaders({
      Accept: API_HEADERS.missionAcceptanceFilesV2
    });

    return this.create(missionAcceptance, url, { headers });
  }

  @ApiAlertError()
  updateMissionAcceptance(missionAcceptance: MissionAcceptance, companyId?: number): Observable<MissionAcceptance> {
    let url = `${this.resourceUrl}/${missionAcceptance.id}`;
    const headers = new HttpHeaders({
      Accept: API_HEADERS.missionAcceptanceFilesV2
    });

    if (companyId) {
      url = HttpHelper.replaceCompanyUrl(url, companyId);
    }

    return this.update(missionAcceptance.id, missionAcceptance, url, { headers });
  }

  @ApiAlertError()
  updateMissionAcceptanceStatus(
    missionAcceptanceId: number,
    status: MissionAcceptanceStatus,
    companyId?: number
  ): Observable<MissionAcceptance> {
    let url = `${this.resourceUrl}/${missionAcceptanceId}/status`;
    const headers = new HttpHeaders({
      Accept: API_HEADERS.missionAcceptanceFilesV2
    });

    if (companyId) {
      url = HttpHelper.replaceCompanyUrl(url, companyId);
    }

    const body: Partial<MissionAcceptanceJson> = {
      id: missionAcceptanceId,
      status
    };

    return this.http
      .patch<MissionAcceptanceJson>(url, body, { headers })
      .pipe(map((json: MissionAcceptanceJson) => this.fromJson(json)));
  }

  @ApiAlertError()
  deleteMissionAcceptance(missionAcceptanceId: number): Observable<unknown> {
    return this.delete(missionAcceptanceId);
  }

  @ApiAlertError()
  downloadMissionAcceptancePdf(missionAcceptanceId: number, companyId?: number): Observable<FileSaverData> {
    let url = `${this.resourceUrl}/${missionAcceptanceId}/pdf`;

    if (companyId) {
      url = HttpHelper.replaceCompanyUrl(url, companyId);
    }

    const blobObservable = this.http.get(url, {
      responseType: 'blob',
      observe: 'response'
    });

    return HttpHelper.getFileSaverData(blobObservable);
  }

  @ApiAlertError()
  addMissionAcceptanceFile(
    missionAcceptanceId: number,
    missionAcceptanceFile: MissionAcceptanceFile,
    companyId?: number
  ): Observable<MissionAcceptanceFile> {
    let url = `${this.resourceUrl}/${missionAcceptanceId}/mission_acceptance_files`;

    if (companyId) {
      url = HttpHelper.replaceCompanyUrl(url, companyId);
    }

    const formData = new FormData();
    formData.append('file', missionAcceptanceFile.file);
    if (missionAcceptanceFile.title) {
      formData.append('title', missionAcceptanceFile.title);
    }
    if (missionAcceptanceFile.type) {
      formData.append('type', missionAcceptanceFile.type);
    }
    if (missionAcceptanceFile.contact) {
      formData.append('contact_id', missionAcceptanceFile.contact.id.toString());
    }

    return this.http
      .post<MissionAcceptanceFileJson>(url, formData)
      .pipe(map((json: MissionAcceptanceFileJson) => MissionAcceptanceFile.fromJson(json)));
  }

  @ApiAlertError()
  addContractsMissionAcceptanceFiles(
    missionAcceptanceId: number,
    files: MissionAcceptanceFile[],
    companyId?: number
  ): Observable<MissionAcceptanceFile[]> {
    let url = `${this.resourceUrl}/${missionAcceptanceId}/contracts`;

    if (companyId) {
      url = HttpHelper.replaceCompanyUrl(url, companyId);
    }

    const body = ArrayHelper.filterDuplicates(files.map((file: MissionAcceptanceFile) => ({ id: file.contractId })));

    return this.http
      .post<MissionAcceptanceFileJson[]>(url, body)
      .pipe(
        map((jsons: MissionAcceptanceFileJson[]) =>
          jsons.map((json: MissionAcceptanceFileJson) => MissionAcceptanceFile.fromJson(json))
        )
      );
  }

  @ApiAlertError()
  downloadMissionAcceptanceFile(
    missionAcceptanceId: number,
    missionAcceptanceFileId: number,
    companyId?: number
  ): Observable<FileSaverData> {
    let url = `${this.resourceUrl}/${missionAcceptanceId}/mission_acceptance_files/${missionAcceptanceFileId}/file`;

    if (companyId) {
      url = HttpHelper.replaceCompanyUrl(url, companyId);
    }

    return HttpHelper.getFileSaverData(this.blobHttpResponse(url));
  }

  @ApiAlertError()
  deleteMissionAcceptanceFile(
    missionAcceptanceId: number,
    missionAcceptanceFileId: number,
    companyId?: number
  ): Observable<void> {
    let url = `${this.resourceUrl}/${missionAcceptanceId}/mission_acceptance_files/${missionAcceptanceFileId}`;

    if (companyId) {
      url = HttpHelper.replaceCompanyUrl(url, companyId);
    }

    return this.http.delete<void>(url);
  }

  @ApiAlertError()
  unlockMissionAcceptance(missionAcceptanceId: number, companyId?: number): Observable<MissionAcceptance> {
    let url = `${this.resourceUrl}/${missionAcceptanceId}/unlock`;

    if (companyId) {
      url = HttpHelper.replaceCompanyUrl(url, companyId);
    }

    return this.http
      .post<MissionAcceptanceJson>(url, null)
      .pipe(map((json: MissionAcceptanceJson) => this.fromJson(json)));
  }
}
